// API клиент для интеграции с backend
// По умолчанию используем относительные URL, чтобы Mini App корректно работал на app.nomafood.ru
// и не был привязан к конкретному домену.
// В dev оставляем прежнее поведение (удобно запускать фронт отдельно без прокси).
const MODE = (import.meta as any).env?.MODE as string | undefined;
const DEFAULT_API_BASE_URL = MODE === 'development' ? 'https://nomafood.ru/api' : '/api';
const DEFAULT_MINIAPP_API_BASE_URL = MODE === 'development' ? 'https://nomafood.ru/api/miniapp' : '/api/miniapp';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || DEFAULT_API_BASE_URL;
const MINIAPP_API_BASE_URL = (import.meta as any).env?.VITE_MINIAPP_API_BASE_URL || DEFAULT_MINIAPP_API_BASE_URL;

export type ApiClientError = Error & {
  status?: number;
  code?: string;
  payload?: any;
};

function createApiClientError(status: number, errorBody: any, fallbackMessage?: string): ApiClientError {
  const message = String(errorBody?.error || fallbackMessage || `HTTP error! status: ${status}`);
  const error = new Error(message) as ApiClientError;
  error.status = status;
  error.code = errorBody?.code ? String(errorBody.code) : undefined;
  error.payload = errorBody;
  return error;
}

export interface MicronutrientsMap {
  calcium?: number;
  iron?: number;
  zinc?: number;
  healthyFats?: number;
  magnesium?: number;
  potassium?: number;
  sodium?: number;
  vitaminC?: number;
  vitaminD?: number;
  vitaminB12?: number;
}

export interface Meal {
  id: string;
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  fiber?: number;
  weight: number;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  date: string;
  time: string;
  userId: string;
  imageUrl?: string;
  description?: string;
  confidence?: number;
  validationLevel?: string;
  crossChecked?: boolean;
}

export interface UserProfile {
  id: string;
  telegramId: number;
  username: string;
  firstName: string;
  lastName?: string;
  weight: number;
  height: number;
  age: number;
  gender: 'male' | 'female';
  activityLevel: 'low' | 'medium' | 'high';
  goalWeight: number;
  goalCalories: number;
  createdAt: string;
  updatedAt: string;
}

export interface DailyStats {
  date: string;
  totalCalories: number;
  totalProtein: number;
  totalCarbs: number;
  totalFats: number;
  totalFiber?: number;
  meals: Meal[];
}

export interface WeeklyStats {
  weekStart: string;
  weekEnd: string;
  averageCalories: number;
  averageProtein: number;
  averageCarbs: number;
  averageFats: number;
  averageFiber?: number;
  totalDays: number;
}

export interface WeightEntry {
  id: string;
  userId: string;
  weight: number;
  date: string;
  bodyFat?: number;
  muscleMass?: number;
  notes?: string;
}

// ===== MINI APP API INTERFACES =====

export interface MiniAppProfile {
  user: {
    id: string;
    telegramId: number;
    username?: string;
    firstName?: string;
    lastName?: string;
    isPremium?: boolean;
    trialEndsAt?: string | null;
    subscriptionEndsAt?: string | null;
  };
  profile: {
    gender: 'male' | 'female';
    age: number;
    birthDate?: string | null;
    weight: number;
    height: number;
    activityLevel: string;
    goal: string;
    goalDetail?: string;
    targetWeight?: number;
    goalStartWeight?: number;
    goalStartedAt?: string | null;
    pace?: string;
    tdee: number;
    protein: number;
    carbs: number;
    fats: number;
    bmi?: number;
    plan?: {
      mode: 'auto' | 'manual';
      adaptationEnabled: boolean;
      status: 'manual' | 'starting' | 'maintain' | 'near_goal' | 'goal_reached' | 'on_track' | 'slower' | 'faster';
      statusLabel: string;
      statusText: string;
      paceLabel?: string | null;
      baseCalories: number;
      effectiveCalories: number;
      currentWeight: number;
      goalWeight?: number | null;
      remainingKg?: number | null;
      weeklyRateKg?: number | null;
      trendConfidence?: 'low' | 'medium' | 'high';
      trendMeasurementsCount?: number;
      trendPeriodDays?: number | null;
      trendDeltaKg?: number | null;
      expectedWeeklyMinKg?: number | null;
      expectedWeeklyMaxKg?: number | null;
      eta?: {
        minWeeks: number;
        maxWeeks: number;
        label: string;
        basedOn: 'initial' | 'trend';
      } | null;
      lastReviewedAt?: string | null;
      lastAdjustedAt?: string | null;
      lastAdjustmentPercent?: number;
    } | null;
    customKBJU?: {
      enabled: boolean;
      calories: number;
      protein: number;
      carbs: number;
      fats: number;
    } | null;

    notificationSettings?: {
      notificationsEnabled: boolean;
      timezone: string;
      weeklyReportsEnabled: boolean;
      mealReminders: Array<{
        id: string;
        hour: number;
        minute: number;
        mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
        enabled: boolean;
      }>;
      macroUnderfill: {
        enabled: boolean;
        hour: number;
        minute: number;
        threshold: number;
      };
      weightTracking: {
        enabled: boolean;
        hour: number;
        minute: number;
        intervalDays: number;
      };
    };
  };
  today: {
    consumed: number;
    remaining: number;
    target: number;
    protein: number;
    carbs: number;
    fats: number;
    fiber?: number;
    micronutrients?: MicronutrientsMap;
  };
  targets?: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
    fiber?: number;
    waterMl?: number;
    micronutrients?: MicronutrientsMap;
  };
  stats?: {
    totalDays: number;
    streak: number;
    longestStreak?: number;
  };
  weightHistory?: Array<{
    date: string;
    weight: number;
    bodyFat?: number;
    muscleMass?: number;
  }>;
}

export interface FrequentMealItem {
  name: string;
  category: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  times: number;
  calories: number;
  protein: number;
  fats: number;
  carbs: number;
  fiber: number;
  weight: number;
  lastAt: string;
}

export interface MiniAppMeal {
  id: string;
  category: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  fiber?: number;
  micronutrients?: MicronutrientsMap;
  weight: number;
  photoUrl?: string;
  aiAnalysis?: any;
  portionAdjustment?: {
    consumedPercent: number;
    adjustmentSource: 'manual';
    adjustedAt: string;
    baseSnapshot?: {
      meal?: {
        calories: number;
        protein: number;
        carbs: number;
        fats: number;
        fiber?: number;
        weight: number;
      };
      dishes?: Array<{
        name: string;
        calories: number;
        protein: number;
        carbs: number;
        fats: number;
        fiber?: number;
        weight: number;
      }>;
    };
  };
  /**
   * Идентификатор группы карточек, порождённых одним анализом фото.
   * Все карточки одного analysisGroupId — это один логический приём пищи
   * на уровне уведомлений/сытости/поиска/экспорта. Для одиночных карточек
   * и ручного ввода — null.
   */
  analysisGroupId?: string | null;
  /** Позиция карточки внутри группы (0..N-1), стабильная сортировка. */
  analysisDishIndex?: number | null;
  createdAt: string;
}

export interface MiniAppStats {
  period: 'day' | 'week' | 'month';
  startDate: string;
  endDate: string;
  targets: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
    fiber?: number;
    waterMl?: number;
    micronutrients?: MicronutrientsMap;
  };
  totals: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
    fiber?: number;
    waterMl?: number;
    micronutrients?: MicronutrientsMap;
  };
  averages: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
    fiber?: number;
    waterMl?: number;
    micronutrients?: MicronutrientsMap;
  };
  dailyStats: Array<{
    date: string;
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
    fiber?: number;
    waterMl?: number;
    mealsCount: number;
  }>;
  mealsCount: number;
  stats?: {
    totalDays: number;
    streak: number;
    longestStreak?: number;
  };
}

export interface MiniAppWaterEntry {
  id: string;
  amountMl: number;
  source: 'quick' | 'manual' | 'reminder';
  recordedAt: string;
  createdAt: string;
}

export interface MiniAppWaterSummary {
  date: string;
  totalMl: number;
  targetMl: number;
  entries: MiniAppWaterEntry[];
}

export interface MiniAppAdviceStatus {
  hasAdvice: boolean;
  generatedAt: string | null;
  nextRefreshAt: string | null;
  localDate: string;
  key: string | null;
}

export interface MiniAppAdvice {
  hasAdvice: boolean;
  title: string;
  tips: string[];
  generatedAt: string;
  nextRefreshAt: string;
  localDate: string;
  key: string;
}

export interface SocialPrivacySettings {
  showWeight: boolean;
  showCalories: boolean;
  showMacros: boolean;
}

export interface SocialFriendCard {
  id: string;
  telegramId: number;
  username?: string | null;
  firstName?: string | null;
  streak: number;
  totalDays: number;
  activeToday: boolean;
  weeklyPoints: number;
  visibleWeight?: number;
  visibleCalories?: number;
  visibleMacros?: {
    protein: number;
    carbs: number;
    fats: number;
  };
}

export interface SocialRequestCard {
  friendshipId: string;
  userId: string;
  username?: string | null;
  firstName?: string | null;
  createdAt: string;
}

export interface SocialLeaderboardEntry {
  userId: string;
  telegramId: number;
  username?: string | null;
  firstName?: string | null;
  isMe: boolean;
  rank: number;
  weeklyPoints: number;
  streak: number;
  totalDays: number;
  activeToday: boolean;
}

export interface ReferralOverview {
  referralCode: string;
  inviteLink: string;
  referralCount: number;
  activatedCount: number;
  pendingCount: number;
  level: number;
  levelName: string;
  nextLevelName: string | null;
  nextLevelRequired: number;
  isAmbassador: boolean;
  totalRewardDays: number;
}

export type SocialActivityFeedItem =
  | {
      id: string;
      userId: string;
      telegramId: number;
      username?: string | null;
      firstName?: string | null;
      type: 'meal';
      createdAt: string;
      mealNames: string[];
      totalCalories: number;
      mealItems?: Array<{
        name: string;
        calories: number;
      }>;
    }
  | {
      id: string;
      userId: string;
      telegramId: number;
      username?: string | null;
      firstName?: string | null;
      type: 'activity';
      createdAt: string;
      activityName: string;
      durationMinutes: number;
      caloriesBurned: number;
    }
  | {
      id: string;
      userId: string;
      telegramId: number;
      username?: string | null;
      firstName?: string | null;
      type: 'calorie_goal';
      createdAt: string;
      calories: number;
      targetCalories: number;
    }
  | {
      id: string;
      userId: string;
      telegramId: number;
      username?: string | null;
      firstName?: string | null;
      type: 'macro_goal';
      createdAt: string;
      protein: number;
      targetProtein: number;
    }
  | {
      id: string;
      userId: string;
      telegramId: number;
      username?: string | null;
      firstName?: string | null;
      type: 'streak';
      createdAt: string;
      streak: number;
    }
  | {
      id: string;
      userId: string;
      telegramId: number;
      username?: string | null;
      firstName?: string | null;
      type: 'achievement';
      createdAt: string;
      achievementTitle: string;
      achievementIcon: string;
    };

export type SocialReactionPromptType =
  | 'calorie_goal'
  | 'macro_goal'
  | 'streak'
  | 'achievement'
  | 'challenge_win'
  | 'challenge_success';

export interface SocialReactionPrompt {
  id: string;
  eventKey: string;
  type: SocialReactionPromptType;
  createdAt: string;
  title: string;
  message: string;
  friend: {
    userId: string;
    telegramId: number | null;
    username?: string | null;
    firstName?: string | null;
    gender?: 'male' | 'female' | null;
  };
  metadata?: Record<string, any>;
}

export type FriendChallengeGoalType =
  | 'calorie_target'
  | 'protein_target'
  | 'activity_competition'
  | 'activity_target';

export type FriendChallengeMode = 'cooperative' | 'competitive';
export type FriendChallengeStatus = 'pending' | 'active' | 'completed' | 'declined' | 'cancelled';

export interface FriendChallengeParticipant {
  userId: string;
  telegramId: number | null;
  username?: string | null;
  firstName?: string | null;
  completedDays: number;
  totalValue: number;
  todayValue: number;
  todayTarget: number | null;
  todayCompleted: boolean;
  daily: Array<{
    localDate: string;
    value: number;
    target: number | null;
    completed: boolean;
  }>;
}

export interface FriendChallengeCard {
  id: string;
  goalType: FriendChallengeGoalType;
  goalLabel: string;
  goalDescription: string;
  mode: FriendChallengeMode;
  status: FriendChallengeStatus;
  createdAt: string;
  startedAt: string | null;
  endsAt: string | null;
  completedAt: string | null;
  inviterId: string;
  inviteeId: string;
  isIncoming: boolean;
  isOutgoing: boolean;
  daysTotal: number;
  daysElapsed: number;
  daysRemaining: number;
  me: FriendChallengeParticipant;
  friend: FriendChallengeParticipant;
  winnerUserId: string | null;
  success: boolean | null;
  canNudge: boolean;
  nudgeBlockedReason: string | null;
}

export interface SocialOverview {
  privacy: SocialPrivacySettings;
  friends: SocialFriendCard[];
  incomingRequests: SocialRequestCard[];
  outgoingRequests: SocialRequestCard[];
  incomingChallenges: FriendChallengeCard[];
  outgoingChallenges: FriendChallengeCard[];
  activeChallenges: FriendChallengeCard[];
  completedChallenges: FriendChallengeCard[];
  leaderboard: SocialLeaderboardEntry[];
  referral: ReferralOverview;
  activityFeed: SocialActivityFeedItem[];
}

export interface AchievementItem {
  key: string;
  title: string;
  description: string;
  icon: string;
  kind: 'core' | 'meme';
  unlocked: boolean;
  progress: number;
  current: number;
  target: number;
  unlockedAt: string | null;
}

export interface AchievementGroup {
  id: 'core' | 'meme';
  title: string;
  items: AchievementItem[];
}

export interface AchievementsOverview {
  summary: {
    unlocked: number;
    total: number;
    memeUnlocked: number;
  };
  groups: AchievementGroup[];
}

// ===== ONBOARDING =====
export interface OnboardingAnswers {
  gender?: 'male' | 'female';
  age?: number;
  birthDate?: string | null;
  weight?: number;
  height?: number;
  activityLevel?: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
  goal?: string;
  goalDetail?: string;
  targetWeight?: number | null;
  pace?: 'soft' | 'balanced' | 'aggressive' | null;
  source?: string | null;
  sourceOther?: string | null;
  referralCode?: string | null;
}

export interface OnboardingPlanPreview {
  calories: number;
  maintenanceCalories: number;
  adjustmentPercent: number | null;
  adjustmentCalories: number | null;
  protein: number;
  carbs: number;
  fats: number;
  fiber: number;
  goal: string;
  goalDetail: string;
  currentWeight: number;
  targetWeight: number;
  remainingKg: number | null;
  weeklyRateKg: number | null;
  paceLabel: string | null;
  statusLabel: string;
  statusText: string;
  eta: { label: string } | null;
}

export interface OnboardingCompleteResult {
  plan: OnboardingPlanPreview;
  trialActivated: boolean;
  referralApplied: boolean;
}

// ===== MEAL PLANNER INTERFACES =====
export type MealPlannerPeriod = 'day' | 'week';
export type MealPlannerDietType = 'regular' | 'vegetarian' | 'dairy_free' | 'gluten_free' | 'high_protein';
export type MealPlannerCookingTime = 'up_to_15' | 'up_to_30' | 'any';
export type MealPlannerBudget = 'economy' | 'medium' | 'any' | 'custom';
export type MealPlannerVarietyMode = 'familiar' | 'balanced' | 'exploratory';
export type ShoppingItemStatus = 'pending' | 'bought' | 'skipped';

export interface MealPlannerProfile {
  dietType: MealPlannerDietType;
  mealsPerDay: 3 | 4 | 5;
  allergies: string[];
  dislikes: string[];
  likes: string[];
  cookingTime: MealPlannerCookingTime;
  budget: MealPlannerBudget;
  varietyMode?: MealPlannerVarietyMode;
  customBudgetAmount?: number | null;
  customBudgetPeriod?: MealPlannerPeriod | null;
}

export interface MealPlannerIngredient {
  name: string;
  quantity: number;
  unit: string;
  category: string;
}

export interface MealPlannerItem {
  id: string;
  dayIndex: number;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  name: string;
  portion: string | null;
  grams: number | null;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  rationale: string;
  ingredients: MealPlannerIngredient[];
  sortOrder: number;
}

export interface MealPlannerShoppingItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  category: string;
  status: ShoppingItemStatus;
}

export interface MealPlannerRecipe {
  itemId: string;
  title: string;
  cookTimeMinutes: number;
  servings: number;
  shortDescription: string;
  ingredients: Array<{
    name: string;
    quantity: number;
    unit: string;
  }>;
  steps: string[];
  tips: string[];
  nutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  };
  source: 'ai' | 'fallback';
}

export interface MealPlannerPlan {
  planId: string;
  period: MealPlannerPeriod;
  startDate: string;
  createdAt: string;
  updatedAt: string;
  source: 'ai' | 'fallback';
  status: 'generating' | 'generated' | 'saved' | 'failed';
  profileConfigured: boolean;
  items: MealPlannerItem[];
  shoppingList: {
    id: string;
    items: MealPlannerShoppingItem[];
  } | null;
  estimatedCost?: {
    total: number;
    perDay: number;
    currency: string;
    confidence: 'low' | 'medium' | 'high';
    comment?: string;
  } | null;
  nutritionSummary?: {
    planTotals: { calories: number; protein: number; carbs: number; fats: number };
    targetTotals: { calories: number; protein: number; carbs: number; fats: number };
    coveragePercent: { calories: number; protein: number; carbs: number; fats: number };
  } | null;
  supplementSuggestions?: Array<{
    type: 'whey' | 'gainer';
    title: string;
    serving: string;
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
    rationale: string;
  }>;
}

// ===== ACTIVITY INTERFACES =====
export type ActivityType = 'running' | 'walking' | 'cycling' | 'swimming' | 'strength' | 'yoga' | 'sports' | 'other';
export type ActivityIntensity = 'low' | 'medium' | 'high';
export type CaloriesMode = 'add_to_norm' | 'show_separate';

export interface MiniAppActivity {
  id: string;
  type: ActivityType;
  name?: string;
  durationMinutes: number;
  intensity: ActivityIntensity;
  caloriesBurned: number;
  recordedAt: string;
  createdAt: string;
}

export interface ActivitiesResponse {
  activities: MiniAppActivity[];
  summary: {
    totalCalories: number;
    totalDuration: number;
    count: number;
  };
  caloriesMode: CaloriesMode;
}

export interface ActivityAiEstimate {
  type: ActivityType;
  name: string;
  durationMinutes: number;
  intensity: ActivityIntensity;
  met: number;
  caloriesBurned: number;
  confidence: number;
}

class ApiClient {
  private baseUrl: string;
  private miniappBaseUrl: string;
  private authToken: string | null = null;
  private refreshPromise: Promise<string> | null = null; // Prevent multiple simultaneous refreshes

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.miniappBaseUrl = MINIAPP_API_BASE_URL;
    this.loadAuthToken();
  }

  private loadAuthToken() {
    this.authToken = localStorage.getItem('miniapp_token');
  }

  private setAuthToken(token: string) {
    this.authToken = token;
    localStorage.setItem('miniapp_token', token);
  }

  setMiniAppOnboardingRequired(required: boolean) {
    localStorage.setItem('miniapp_onboarding_required', required ? 'true' : 'false');
    try {
      window.dispatchEvent(new Event('miniapp_onboarding_required_changed'));
    } catch {
      // noop
    }
  }

  getMiniAppOnboardingRequired(): boolean {
    return localStorage.getItem('miniapp_onboarding_required') === 'true';
  }

  resetMiniAppAuth() {
    this.clearAuthToken();
    this.setMiniAppOnboardingRequired(false);
  }

  private clearAuthToken() {
    this.authToken = null;
    localStorage.removeItem('miniapp_token');
  }

  private async refreshToken(): Promise<string | null> {
    // If already refreshing, wait for that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const tg = (window as any).Telegram?.WebApp;
    const initData = tg?.initData;

    if (!initData) {
      console.warn('⚠️ No initData available for token refresh');
      return null;
    }

    // Create refresh promise to prevent parallel refreshes
    this.refreshPromise = this.startMiniAppSession(initData)
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let errorBody: any = null;
        try {
          errorBody = await response.clone().json();
        } catch {
          // ignore
        }
        throw createApiClientError(response.status, errorBody);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  private async miniappRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.miniappBaseUrl}${endpoint}`;

    if (!this.authToken) {
      try {
        const tg = (window as any).Telegram?.WebApp;
        const initData = tg?.initData;
        if (initData) {
          await this.startMiniAppSession(initData);
        }
      } catch (error) {
        console.warn('⚠️ Failed to lazily start Mini App session', error);
      }
    }

    // Use current token
    const token = this.authToken || localStorage.getItem('miniapp_token');

    const baseHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token && !(options.headers as any)?.Authorization) {
      baseHeaders['Authorization'] = `Bearer ${token}`;
    }

    // Add Telegram ID header for backend validation
    try {
      const tg = (window as any).Telegram?.WebApp;
      const tgId = tg?.initDataUnsafe?.user?.id;
      if (tgId && !baseHeaders['x-telegram-id']) {
        baseHeaders['x-telegram-id'] = String(tgId);
      }
    } catch (_) { /* noop */ }

    const config: RequestInit = {
      ...options,
      cache: 'no-store',
      headers: {
        ...baseHeaders,
        ...options.headers,
      },
    };

    let response = await fetch(url, config);

    // If unauthorized, check if token expired and refresh
    if (response.status === 401) {
      let shouldRetry = false;
      try {
        const errorBody = await response.clone().json();
        // Retry only if token expired (not if invalid/mismatch)
        if (errorBody?.code === 'TOKEN_EXPIRED' || errorBody?.error === 'Token expired') {
          console.log('🔄 Token expired, refreshing...');
          shouldRetry = true;
        } else if (errorBody?.code === 'TOKEN_MISMATCH' || errorBody?.code === 'TOKEN_INVALID') {
          // Typical case: user switched Telegram accounts; re-auth with current initData
          this.clearAuthToken();
          shouldRetry = true;
        } else {
          console.warn('⚠️ Auth failed (not expired):', errorBody);
        }
      } catch {
        // If can't parse JSON, try refresh anyway
        shouldRetry = true;
      }

      if (shouldRetry) {
        const newToken = await this.refreshToken();
        if (newToken) {
          const retryHeaders = {
            ...(config.headers as any),
            Authorization: `Bearer ${newToken}`,
          };
          response = await fetch(url, { ...config, headers: retryHeaders });
        }
      }
    }

    if (!response.ok) {
      let errorBody: any = null;
      try {
        errorBody = await response.clone().json();
      } catch {
        // ignore
      }

      if (response.status === 403 && errorBody?.code === 'ONBOARDING_REQUIRED') {
        this.setMiniAppOnboardingRequired(true);
      }

      if (response.status === 404 && (errorBody?.code === 'PROFILE_NOT_FOUND' || /user not found/i.test(String(errorBody?.error || '')))) {
        this.setMiniAppOnboardingRequired(true);
      }

      const errorText = errorBody ? JSON.stringify(errorBody) : await response.text();
      console.error('❌ miniappRequest failed:', { status: response.status, errorText });
      throw createApiClientError(response.status, errorBody);
    }
    return (await response.json()) as T;
  }

  // ===== USER PROFILE API =====

  /**
   * Получение профиля пользователя по Telegram ID
   */
  async getUserProfile(telegramId: number): Promise<UserProfile> {
    const response = await this.request<{ success: boolean; data: UserProfile }>(
      `/users/telegram/${telegramId}`
    );
    return response.data;
  }

  /**
   * Обновление профиля пользователя
   */
  async updateUserProfile(userId: string, profileData: Partial<UserProfile>): Promise<UserProfile> {
    const response = await this.request<{ success: boolean; data: UserProfile }>(
      `/users/${userId}`,
      {
        method: 'PUT',
        body: JSON.stringify(profileData),
      }
    );
    return response.data;
  }

  // ===== MEALS API =====

  /**
   * Получение приемов пищи пользователя за день
   */
  async getDailyMeals(userId: string, date: string): Promise<Meal[]> {
    const response = await this.request<{ success: boolean; data: Meal[] }>(
      `/meals/user/${userId}/daily?date=${date}`
    );
    return response.data;
  }

  /**
   * Получение приемов пищи пользователя за неделю
   */
  async getWeeklyMeals(userId: string, startDate: string): Promise<Meal[]> {
    const response = await this.request<{ success: boolean; data: Meal[] }>(
      `/meals/user/${userId}/weekly?startDate=${startDate}`
    );
    return response.data;
  }

  /**
   * Создание нового приема пищи
   */
  async createMeal(userId: string, mealData: Omit<Meal, 'id' | 'userId'>): Promise<Meal> {
    const response = await this.request<{ success: boolean; data: Meal }>(
      '/meals',
      {
        method: 'POST',
        body: JSON.stringify({ ...mealData, userId }),
      }
    );
    return response.data;
  }

  /**
   * Обновление приема пищи
   */
  async updateMeal(mealId: string, mealData: Partial<Meal>): Promise<Meal> {
    const response = await this.request<{ success: boolean; data: Meal }>(
      `/meals/${mealId}`,
      {
        method: 'PUT',
        body: JSON.stringify(mealData),
      }
    );
    return response.data;
  }

  /**
   * Удаление приема пищи
   */
  async deleteMeal(mealId: string): Promise<void> {
    await this.request(`/meals/${mealId}`, {
      method: 'DELETE',
    });
  }

  // ===== STATISTICS API =====

  /**
   * Получение статистики за день
   */
  async getDailyStats(userId: string, date: string): Promise<DailyStats> {
    const response = await this.request<{ success: boolean; data: DailyStats }>(
      `/stats/daily/${userId}?date=${date}`
    );
    return response.data;
  }

  /**
   * Получение статистики за неделю
   */
  async getWeeklyStats(userId: string, startDate: string): Promise<WeeklyStats> {
    const response = await this.request<{ success: boolean; data: WeeklyStats }>(
      `/stats/weekly/${userId}?startDate=${startDate}`
    );
    return response.data;
  }

  // ===== WEIGHT TRACKING API =====

  /**
   * Получение записей веса пользователя
   */
  async getWeightHistory(userId: string, limit: number = 30): Promise<WeightEntry[]> {
    const response = await this.request<{ success: boolean; data: WeightEntry[] }>(
      `/users/${userId}/weight?limit=${limit}`
    );
    return response.data;
  }

  /**
   * Добавление новой записи веса
   */
  async addWeightEntry(userId: string, weight: number, date?: string): Promise<WeightEntry> {
    const response = await this.request<{ success: boolean; data: WeightEntry }>(
      `/users/${userId}/weight`,
      {
        method: 'POST',
        body: JSON.stringify({ weight, date: date || new Date().toISOString() }),
      }
    );
    return response.data;
  }

  /**
   * Добавить запись веса по telegramId текущего пользователя (удобно из MiniApp)
   */
  async addMyWeight(weight: number, date?: string): Promise<{ data: WeightEntry; goalReached?: boolean; goalMessage?: string }> {
    if (!this.authToken) throw new Error('No auth token available');

    // Убедимся, что вес - число
    const numWeight = typeof weight === 'string' ? parseFloat(weight) : weight;
    if (!Number.isFinite(numWeight) || numWeight <= 0 || numWeight > 500) {
      throw new Error('Invalid weight value');
    }

    console.log('📤 Sending weight to API:', { weight: numWeight, date });

    const res = await this.miniappRequest<{ success: boolean; data: WeightEntry; goalReached?: boolean; goalMessage?: string }>(
      '/weight',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.authToken}` },
        body: JSON.stringify({ weight: numWeight, date })
      }
    );

    console.log('✅ Weight API response:', res);
    return { data: res.data, goalReached: res.goalReached, goalMessage: res.goalMessage };
  }

  // ===== SYNC API =====

  /**
   * Получение полных данных пользователя для синхронизации
   */
  async getFullUserData(telegramId: number): Promise<{
    user: UserProfile;
    todayMeals: Meal[];
    weeklyStats: WeeklyStats;
    weightHistory: WeightEntry[];
  }> {
    const response = await this.request<{
      success: boolean;
      data: {
        user: UserProfile;
        todayMeals: Meal[];
        weeklyStats: WeeklyStats;
        weightHistory: WeightEntry[];
      };
    }>(`/sync/user/${telegramId}`);
    return response.data;
  }

  /**
   * Синхронизация данных между приложением и ботом
   */
  async syncData(telegramId: number, data: {
    meals?: Meal[];
    weightEntries?: WeightEntry[];
    profileUpdates?: Partial<UserProfile>;
  }): Promise<{
    success: boolean;
    message: string;
    syncedItems: number;
  }> {
    const response = await this.request<{
      success: boolean;
      data: {
        message: string;
        syncedItems: number;
      };
    }>(`/sync/user/${telegramId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return {
      success: response.success,
      message: response.data.message,
      syncedItems: response.data.syncedItems,
    };
  }

  // ===== TELEGRAM INTEGRATION =====

  /**
   * Получение Telegram WebApp данных
   */
  getTelegramData(): { initData: string; user?: any } | null {
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp as any;
      return {
        initData: tg.initData || '',
        user: tg.initDataUnsafe?.user,
      };
    }
    return null;
  }

  /**
   * Получение Telegram ID пользователя
   */
  getTelegramUserId(): number | null {
    const tgData = this.getTelegramData();
    return tgData?.user?.id || null;
  }

  // ===== MINI APP API =====

  /**
   * Получение dev токена для локального тестирования
   */
  async getDevToken(telegramId: number, firstName: string = 'Test', username: string = 'testuser'): Promise<string> {
    const response = await this.miniappRequest<{ success: boolean; token: string; onboardingRequired?: boolean }>(
      '/session/dev',
      {
        method: 'POST',
        body: JSON.stringify({ telegramId, firstName, username }),
      }
    );
    this.setMiniAppOnboardingRequired(!!response.onboardingRequired);
    this.setAuthToken(response.token);
    return response.token;
  }

  /**
   * Старт сессии Mini App через Telegram initData
   */
  async startMiniAppSession(initData: string): Promise<string> {
    const response = await this.miniappRequest<{ success: boolean; token: string; onboardingRequired?: boolean }>(
      '/session/start',
      {
        method: 'POST',
        body: JSON.stringify({ initData })
      }
    );
    // Важно: обновляем внутренний токен, а не только localStorage
    this.setMiniAppOnboardingRequired(!!response.onboardingRequired);
    this.setAuthToken(response.token);
    return response.token;
  }

  /**
   * Живой предпросмотр плана по ответам анкеты (ничего не сохраняет)
   */
  async previewOnboarding(answers: OnboardingAnswers): Promise<OnboardingPlanPreview> {
    const response = await this.miniappRequest<{ success: boolean; data: { plan: OnboardingPlanPreview } }>(
      '/onboarding/preview',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify(answers),
      }
    );
    return response.data.plan;
  }

  /**
   * Завершение анкеты в Mini App: создаёт профиль, включает триал
   */
  async completeOnboarding(answers: OnboardingAnswers): Promise<OnboardingCompleteResult> {
    const response = await this.miniappRequest<{ success: boolean; data: OnboardingCompleteResult }>(
      '/onboarding/complete',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify(answers),
      }
    );
    // Не сбрасываем флаг здесь: иначе AppV2 выйдет из визарда до показа плана.
    // Флаг снимается уже после нажатия «Открыть дневник» (см. onCompleted).
    return response.data;
  }

  /**
   * Получение профиля пользователя через Mini App API
   */
  async getMiniAppProfile(): Promise<MiniAppProfile> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: MiniAppProfile }>(
      '/me',
      {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Получение приёмов пищи через Mini App API
   */
  async getMiniAppMeals(date?: string, limit: number = 20, offset: number = 0): Promise<{
    meals: MiniAppMeal[];
    pagination: {
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (date) {
      params.append('date', date);
    }

    const response = await this.miniappRequest<{ success: boolean; data: any }>(
      `/meals?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Создание приёма пищи через Mini App API
   */
  async createMiniAppMeal(mealData: {
    category: 'breakfast' | 'lunch' | 'dinner' | 'snack';
    name: string;
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
    weight?: number;
  }): Promise<MiniAppMeal> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { meal: MiniAppMeal } }>(
      '/meals',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
        body: JSON.stringify(mealData),
      }
    );
    return response.data.meal;
  }

  /**
   * Частые блюда за последние 30 дней (для быстрого повтора).
   */
  async getFrequentMealsMiniApp(limit: number = 6): Promise<FrequentMealItem[]> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { items: FrequentMealItem[] } }>(
      `/meals/frequent?limit=${limit}`,
      {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data.items;
  }

  /**
   * Экспорт данных пользователя: бот присылает CSV-файлы в чат.
   */
  async exportMyDataMiniApp(): Promise<{ files: number; message?: string }> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { files: number; message?: string } }>(
      '/export',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Анализ текстового описания еды (добавление приёма пищи текстом из мини-аппа).
   */
  async analyzeMealTextMiniApp(text: string): Promise<{ success: true; analysis: any } | { success: false; error: string; message?: string }> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }
    const response = await this.miniappRequest<{ success: true; data: { analysis: any } } | { success: false; error: string; message?: string }>(
      '/meals/analyze-text',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify({ text }),
      }
    );
    if (!response.success) return response as { success: false; error: string; message?: string };
    return { success: true, analysis: (response as any).data.analysis };
  }

  async transcribeMealAudioMiniApp(file: File, durationSeconds: number): Promise<
    { success: true; text: string } | { success: false; error: string; message?: string }
  > {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('durationSeconds', String(Math.max(0, Math.round(durationSeconds))));

    const response = await fetch(`${this.miniappBaseUrl}/meals/transcribe-audio`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Authorization': `Bearer ${this.authToken}` },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      return { success: false, error: data?.error || 'transcription_failed', message: data?.message };
    }
    return { success: true, text: String(data.data?.text || '').trim() };
  }

  /**
   * Анализ фото еды, загруженного из мини-аппа (камера/галерея).
   * multipart/form-data — отдельный вызов fetch напрямую (не через miniappRequest),
   * чтобы браузер сам выставил Content-Type с boundary для FormData.
   */
  async analyzeMealPhotoMiniApp(file: File, hint?: string): Promise<
    { success: true; analysis: any; photoToken: string } | { success: false; error: string; message?: string }
  > {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }
    const formData = new FormData();
    formData.append('photo', file);
    if (hint) formData.append('hint', hint);

    const response = await fetch(`${this.miniappBaseUrl}/meals/analyze-photo`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Authorization': `Bearer ${this.authToken}` },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok || !data?.success) {
      return { success: false, error: data?.error || 'analysis_failed', message: data?.message };
    }
    return { success: true, analysis: data.data.analysis, photoToken: data.data.photoToken };
  }

  /**
   * Правка анализа ДО сохранения: изменить вес ингредиента / удалить / уточнить через ИИ.
   */
  async editMealAnalysisMiniApp(payload: {
    analysis: any;
    action: 'set_weight' | 'delete' | 'refine';
    path?: { kind: 'item'; index: number } | { kind: 'dishItem'; dishIndex: number; itemIndex: number };
    weightGrams?: number;
    scope?: 'whole_meal' | 'selected_dish' | 'selected_item';
    userText?: string;
    dishIndex?: number;
  }): Promise<{ success: true; analysis: any } | { success: false; error: string }> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }
    const response = await this.miniappRequest<{ success: true; data: { analysis: any } } | { success: false; error: string }>(
      '/meals/analysis/edit',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify(payload),
      }
    );
    if (!response.success) return response as { success: false; error: string };
    return { success: true, analysis: (response as any).data.analysis };
  }

  /**
   * Сохранение готового анализа как одного или нескольких приёмов пищи.
   */
  async saveMealAnalysisMiniApp(payload: {
    analysis: any;
    category: 'breakfast' | 'lunch' | 'dinner' | 'snack';
    photoToken?: string;
  }): Promise<{ analysisGroupId: string | null; meals: MiniAppMeal[] }> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }
    const response = await this.miniappRequest<{ success: boolean; data: { analysisGroupId: string | null; meals: MiniAppMeal[] } }>(
      '/meals/analysis/save',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify(payload),
      }
    );
    return response.data;
  }

  /**
   * Удаление приёма пищи через Mini App API
   */
  async deleteMiniAppMeal(mealId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean; message: string }>(
      `/meals/${mealId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
  }

  async updateMiniAppMealPortion(mealId: string, consumedPercent: number): Promise<MiniAppMeal & { dishes?: Array<any> }> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { meal: MiniAppMeal & { dishes?: Array<any> } } }>(
      `/meals/${mealId}/portion`,
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
        body: JSON.stringify({ consumedPercent }),
      }
    );
    return response.data.meal;
  }

  async updateMiniAppMealWeight(
    mealId: string,
    target: { kind: 'meal' } | { kind: 'ingredient'; index: number },
    weightGrams: number,
  ): Promise<MiniAppMeal & { dishes?: Array<any> }> {
    if (!this.authToken) throw new Error('No auth token available');
    const response = await this.miniappRequest<{ success: boolean; data: { meal: MiniAppMeal & { dishes?: Array<any> } } }>(
      `/meals/${mealId}/weight`,
      {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify({ target, weightGrams }),
      }
    );
    return response.data.meal;
  }

  /**
   * Получение статистики через Mini App API
   */
  async getMiniAppStats(period: 'day' | 'week' | 'month' = 'week', date?: string): Promise<MiniAppStats> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const qs = new URLSearchParams({ period });
    if (date) qs.set('date', date);

    const response = await this.miniappRequest<{ success: boolean; data: MiniAppStats }>(
      `/stats?${qs.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Статус советов (есть ли подсказки прямо сейчас)
   */
  async getMiniAppAdviceStatus(): Promise<MiniAppAdviceStatus> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: MiniAppAdviceStatus }>(
      '/advice/status',
      {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  /**
   * Получить советы (3 подсказки на сегодня)
   */
  async getMiniAppAdvice(): Promise<MiniAppAdvice> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: MiniAppAdvice }>(
      '/advice',
      {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  async getSocialOverview(): Promise<SocialOverview> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: SocialOverview }>(
      '/social/overview',
      {
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
    return response.data;
  }

  async getSocialPrompts(): Promise<SocialReactionPrompt[]> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: SocialReactionPrompt[] }>(
      '/social/prompts',
      {
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
    return response.data;
  }

  async dismissSocialPrompt(promptId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean }>(
      `/social/prompts/${promptId}/dismiss`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
  }

  async congratulateSocialPrompt(promptId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean }>(
      `/social/prompts/${promptId}/congratulate`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
  }

  async requestFriend(username: string): Promise<any> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: any }>(
      '/social/request',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify({ username }),
      }
    );
    return response.data;
  }

  async acceptFriendRequest(friendshipId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean }>(
      `/social/requests/${friendshipId}/accept`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
  }

  async declineFriendRequest(friendshipId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean }>(
      `/social/requests/${friendshipId}/decline`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
  }

  async removeFriend(friendUserId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean }>(
      `/social/friends/${friendUserId}`,
      {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
  }

  async updateSocialPrivacy(data: Partial<SocialPrivacySettings>): Promise<SocialPrivacySettings> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: SocialPrivacySettings }>(
      '/social/privacy',
      {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify(data),
      }
    );
    return response.data;
  }

  async createFriendChallenge(friendUserId: string, goalType: FriendChallengeGoalType): Promise<FriendChallengeCard> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: FriendChallengeCard }>(
      '/social/challenges',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify({ friendUserId, goalType }),
      }
    );
    return response.data;
  }

  async acceptFriendChallenge(challengeId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean }>(
      `/social/challenges/${challengeId}/accept`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
  }

  async declineFriendChallenge(challengeId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean }>(
      `/social/challenges/${challengeId}/decline`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
  }

  async sendFriendChallengeNudge(challengeId: string): Promise<FriendChallengeCard> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: FriendChallengeCard }>(
      `/social/challenges/${challengeId}/nudge`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
    return response.data;
  }

  async cancelFriendChallenge(challengeId: string): Promise<FriendChallengeCard> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: FriendChallengeCard }>(
      `/social/challenges/${challengeId}/cancel`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
    return response.data;
  }

  async getAchievements(): Promise<AchievementsOverview> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: AchievementsOverview }>(
      '/achievements',
      {
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
    return response.data;
  }

  // ===== MEAL PLANNER API =====

  async getMealPlannerProfile(): Promise<{ profileConfigured: boolean; profile: MealPlannerProfile | null }> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { profileConfigured: boolean; profile: MealPlannerProfile | null } }>(
      '/meal-planner/profile',
      {
        headers: {
          Authorization: `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  async updateMealPlannerProfile(payload: MealPlannerProfile): Promise<{ profileConfigured: boolean; profile: MealPlannerProfile }> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { profileConfigured: boolean; profile: MealPlannerProfile } }>(
      '/meal-planner/profile',
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${this.authToken}`,
        },
        body: JSON.stringify(payload),
      }
    );
    return response.data;
  }

  async getMealPlannerPlan(period: MealPlannerPeriod): Promise<{ profileConfigured: boolean; plan: MealPlannerPlan | null }> {
    const qs = new URLSearchParams({
      period,
      _ts: String(Date.now()),
    });
    const response = await this.miniappRequest<{ success: boolean; data: { profileConfigured: boolean; plan: MealPlannerPlan | null } }>(
      `/meal-planner/plan?${qs.toString()}`
    );
    return response.data;
  }

  async generateMealPlannerPlan(period: MealPlannerPeriod): Promise<MealPlannerPlan> {
    const response = await this.miniappRequest<{ success: boolean; data: { plan: MealPlannerPlan } }>(
      '/meal-planner/generate',
      {
        method: 'POST',
        body: JSON.stringify({ period }),
      }
    );
    return response.data.plan;
  }

  async replaceMealPlannerItem(payload: { itemId: string; planId?: string; reason?: string }): Promise<MealPlannerPlan> {
    const response = await this.miniappRequest<{ success: boolean; data: { plan: MealPlannerPlan } }>(
      '/meal-planner/replace-item',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    return response.data.plan;
  }

  async getMealPlannerRecipe(itemId: string, planId?: string): Promise<MealPlannerRecipe> {
    const qs = planId ? `?planId=${encodeURIComponent(planId)}` : '';
    const response = await this.miniappRequest<{ success: boolean; data: { recipe: MealPlannerRecipe } }>(
      `/meal-planner/recipe/${itemId}${qs}`,
      {}
    );
    return response.data.recipe;
  }

  async getMealPlannerShoppingList(period?: MealPlannerPeriod): Promise<{ shoppingList: MealPlannerPlan['shoppingList']; plan: MealPlannerPlan | null }> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const qs = period ? `?period=${period}` : '';
    const response = await this.miniappRequest<{ success: boolean; data: { shoppingList: MealPlannerPlan['shoppingList']; plan: MealPlannerPlan | null } }>(
      `/meal-planner/shopping-list${qs}`,
      {
        headers: {
          Authorization: `Bearer ${this.authToken}`,
        },
      }
    );
    return response.data;
  }

  async updateMealPlannerShoppingItemStatus(itemId: string, status: ShoppingItemStatus): Promise<MealPlannerShoppingItem> {
    const response = await this.miniappRequest<{ success: boolean; data: { item: MealPlannerShoppingItem } }>(
      `/meal-planner/shopping-list/items/${itemId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ status }),
      }
    );
    return response.data.item;
  }

  /**
   * Проверка наличия токена авторизации
   */
  hasAuthToken(): boolean {
    return !!this.authToken;
  }

  /**
   * Получение текущего токена
   */
  getAuthToken(): string | null {
    return this.authToken;
  }

  // ===== WATER API =====

  async getWater(date?: string): Promise<MiniAppWaterSummary> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const params = date ? `?date=${date}` : '';
    const response = await this.miniappRequest<{ success: boolean; data: MiniAppWaterSummary }>(
      `/water${params}`,
      {
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
    return response.data;
  }

  async addWater(amountMl: number, recordedAt?: string): Promise<MiniAppWaterEntry> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { entry: MiniAppWaterEntry } }>(
      '/water',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify({ amountMl, recordedAt }),
      }
    );
    return response.data.entry;
  }

  async deleteWaterEntry(entryId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean; message: string }>(
      `/water/${entryId}`,
      {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
  }

  // ===== ACTIVITIES API =====

  /**
   * Получение активностей за день
   */
  async getActivities(date?: string): Promise<ActivitiesResponse> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const params = date ? `?date=${date}` : '';
    // Backend returns { activities, stats, caloriesMode } (legacy frontend expects { activities, summary, caloriesMode })
    const response = await this.miniappRequest<{ success: boolean; data: any }>(
      `/activities${params}`,
      {
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );

    const raw = response.data || {};
    const stats = raw.stats || {};
    const summary = raw.summary || {
      totalCalories: Number(stats.totalCaloriesBurned ?? stats.totalCalories ?? 0),
      totalDuration: Number(stats.totalDurationMinutes ?? stats.totalDuration ?? 0),
      count: Number(stats.activitiesCount ?? stats.count ?? 0),
    };

    return {
      activities: raw.activities || [],
      summary,
      caloriesMode: (raw.caloriesMode as CaloriesMode) || 'show_separate',
    };
  }

  /**
   * Создание активности
   */
  async createActivity(activityData: {
    type: ActivityType;
    name?: string;
    durationMinutes: number;
    intensity?: ActivityIntensity;
    met?: number;
    recordedAt?: string;
  }): Promise<MiniAppActivity> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { activity: MiniAppActivity } }>(
      '/activities',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify(activityData),
      }
    );
    return response.data.activity;
  }

  /**
   * Обновление активности
   */
  async updateActivity(activityId: string, activityData: {
    type?: ActivityType;
    name?: string;
    durationMinutes?: number;
    intensity?: ActivityIntensity;
    met?: number;
    recordedAt?: string;
  }): Promise<MiniAppActivity> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { activity: MiniAppActivity } }>(
      `/activities/${activityId}`,
      {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify(activityData),
      }
    );
    return response.data.activity;
  }

  /**
   * Удаление активности
   */
  async deleteActivity(activityId: string): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean }>(
      `/activities/${activityId}`,
      {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
  }

  /**
   * Получение режима калорий для активностей
   */
  async getCaloriesMode(): Promise<CaloriesMode> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { mode: CaloriesMode } }>(
      '/activities/calories-mode',
      {
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
    return response.data.mode;
  }

  /**
   * Установка режима калорий для активностей
   */
  async setCaloriesMode(mode: CaloriesMode): Promise<void> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    await this.miniappRequest<{ success: boolean }>(
      '/activities/calories-mode',
      {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify({ mode }),
      }
    );
  }

  /**
   * Расчёт калорий для активности (предпросмотр)
   */
  async calculateActivityCalories(type: ActivityType, durationMinutes: number, intensity: ActivityIntensity = 'medium'): Promise<number> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: { calories: number } }>(
      `/activities/calculate?type=${type}&durationMinutes=${durationMinutes}&intensity=${intensity}`,
      {
        headers: { 'Authorization': `Bearer ${this.authToken}` },
      }
    );
    return response.data.calories;
  }

  /**
   * AI-парсинг свободного текста активности (возвращает оценку + калории)
   */
  async estimateActivityFromText(text: string): Promise<ActivityAiEstimate> {
    if (!this.authToken) {
      throw new Error('No auth token available');
    }

    const response = await this.miniappRequest<{ success: boolean; data: ActivityAiEstimate }>(
      '/activities/ai-estimate',
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${this.authToken}` },
        body: JSON.stringify({ text }),
      }
    );
    return response.data;
  }
}

// Создаем экземпляр API клиента
export const apiClient = new ApiClient();

const apiHooks = {
  // Generic request method for custom endpoints
  request: async (endpoint: string, options: RequestInit = {}): Promise<any> => {
    const token = localStorage.getItem('miniapp_token');
    const baseHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      baseHeaders['Authorization'] = `Bearer ${token}`;
    }
    try {
      const tg = (window as any).Telegram?.WebApp;
      const tgId = tg?.initDataUnsafe?.user?.id;
      if (tgId) {
        baseHeaders['x-telegram-id'] = String(tgId);
      }
    } catch (_) { /* noop */ }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: { ...baseHeaders, ...options.headers },
    });
    return response.json();
  },

  // User Profile
  getUserProfile: apiClient.getUserProfile.bind(apiClient),
  updateUserProfile: apiClient.updateUserProfile.bind(apiClient),

  // Meals
  getDailyMeals: apiClient.getDailyMeals.bind(apiClient),
  getWeeklyMeals: apiClient.getWeeklyMeals.bind(apiClient),
  createMeal: apiClient.createMeal.bind(apiClient),
  updateMeal: apiClient.updateMeal.bind(apiClient),
  deleteMeal: apiClient.deleteMeal.bind(apiClient),

  // Statistics
  getDailyStats: apiClient.getDailyStats.bind(apiClient),
  getWeeklyStats: apiClient.getWeeklyStats.bind(apiClient),

  // Weight Tracking
  getWeightHistory: apiClient.getWeightHistory.bind(apiClient),
  addWeightEntry: apiClient.addWeightEntry.bind(apiClient),
  addMyWeight: apiClient.addMyWeight.bind(apiClient),

  // Sync
  getFullUserData: apiClient.getFullUserData.bind(apiClient),
  syncData: apiClient.syncData.bind(apiClient),

  // Telegram
  getTelegramData: apiClient.getTelegramData.bind(apiClient),
  getTelegramUserId: apiClient.getTelegramUserId.bind(apiClient),

  // Mini App API
  getDevToken: apiClient.getDevToken.bind(apiClient),
  startMiniAppSession: apiClient.startMiniAppSession.bind(apiClient),
  getMiniAppProfile: apiClient.getMiniAppProfile.bind(apiClient),
  getMiniAppMeals: apiClient.getMiniAppMeals.bind(apiClient),
  createMiniAppMeal: apiClient.createMiniAppMeal.bind(apiClient),
  exportMyDataMiniApp: apiClient.exportMyDataMiniApp.bind(apiClient),
  getFrequentMealsMiniApp: apiClient.getFrequentMealsMiniApp.bind(apiClient),
  analyzeMealTextMiniApp: apiClient.analyzeMealTextMiniApp.bind(apiClient),
  transcribeMealAudioMiniApp: apiClient.transcribeMealAudioMiniApp.bind(apiClient),
  analyzeMealPhotoMiniApp: apiClient.analyzeMealPhotoMiniApp.bind(apiClient),
  editMealAnalysisMiniApp: apiClient.editMealAnalysisMiniApp.bind(apiClient),
  saveMealAnalysisMiniApp: apiClient.saveMealAnalysisMiniApp.bind(apiClient),
  deleteMiniAppMeal: apiClient.deleteMiniAppMeal.bind(apiClient),
  updateMiniAppMealPortion: apiClient.updateMiniAppMealPortion.bind(apiClient),
  updateMiniAppMealWeight: apiClient.updateMiniAppMealWeight.bind(apiClient),
  getMiniAppStats: apiClient.getMiniAppStats.bind(apiClient),
  getMiniAppAdviceStatus: apiClient.getMiniAppAdviceStatus.bind(apiClient),
  getMiniAppAdvice: apiClient.getMiniAppAdvice.bind(apiClient),
  getSocialOverview: apiClient.getSocialOverview.bind(apiClient),
  getSocialPrompts: apiClient.getSocialPrompts.bind(apiClient),
  dismissSocialPrompt: apiClient.dismissSocialPrompt.bind(apiClient),
  congratulateSocialPrompt: apiClient.congratulateSocialPrompt.bind(apiClient),
  requestFriend: apiClient.requestFriend.bind(apiClient),
  acceptFriendRequest: apiClient.acceptFriendRequest.bind(apiClient),
  declineFriendRequest: apiClient.declineFriendRequest.bind(apiClient),
  removeFriend: apiClient.removeFriend.bind(apiClient),
  createFriendChallenge: apiClient.createFriendChallenge.bind(apiClient),
  acceptFriendChallenge: apiClient.acceptFriendChallenge.bind(apiClient),
  declineFriendChallenge: apiClient.declineFriendChallenge.bind(apiClient),
  sendFriendChallengeNudge: apiClient.sendFriendChallengeNudge.bind(apiClient),
  cancelFriendChallenge: apiClient.cancelFriendChallenge.bind(apiClient),
  updateSocialPrivacy: apiClient.updateSocialPrivacy.bind(apiClient),
  getAchievements: apiClient.getAchievements.bind(apiClient),
  previewOnboarding: apiClient.previewOnboarding.bind(apiClient),
  completeOnboarding: apiClient.completeOnboarding.bind(apiClient),
  setMiniAppOnboardingRequired: apiClient.setMiniAppOnboardingRequired.bind(apiClient),
  getMealPlannerProfile: apiClient.getMealPlannerProfile.bind(apiClient),
  updateMealPlannerProfile: apiClient.updateMealPlannerProfile.bind(apiClient),
  getMealPlannerPlan: apiClient.getMealPlannerPlan.bind(apiClient),
  generateMealPlannerPlan: apiClient.generateMealPlannerPlan.bind(apiClient),
  replaceMealPlannerItem: apiClient.replaceMealPlannerItem.bind(apiClient),
  getMealPlannerRecipe: apiClient.getMealPlannerRecipe.bind(apiClient),
  getMealPlannerShoppingList: apiClient.getMealPlannerShoppingList.bind(apiClient),
  updateMealPlannerShoppingItemStatus: apiClient.updateMealPlannerShoppingItemStatus.bind(apiClient),
  getMiniAppOnboardingRequired: apiClient.getMiniAppOnboardingRequired.bind(apiClient),
  resetMiniAppAuth: apiClient.resetMiniAppAuth.bind(apiClient),
  hasAuthToken: apiClient.hasAuthToken.bind(apiClient),
  getAuthToken: apiClient.getAuthToken.bind(apiClient),

  // Water
  getWater: apiClient.getWater.bind(apiClient),
  addWater: apiClient.addWater.bind(apiClient),
  deleteWaterEntry: apiClient.deleteWaterEntry.bind(apiClient),

  // Activities
  getActivities: apiClient.getActivities.bind(apiClient),
  createActivity: apiClient.createActivity.bind(apiClient),
  updateActivity: apiClient.updateActivity.bind(apiClient),
  deleteActivity: apiClient.deleteActivity.bind(apiClient),
  getCaloriesMode: apiClient.getCaloriesMode.bind(apiClient),
  setCaloriesMode: apiClient.setCaloriesMode.bind(apiClient),
  calculateActivityCalories: apiClient.calculateActivityCalories.bind(apiClient),
  estimateActivityFromText: apiClient.estimateActivityFromText.bind(apiClient),
};

// Хук для работы с API в React компонентах
export function useApi() {
  return apiHooks;
}

export default apiClient; 
